import { PlusIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useState } from 'react';

import type { WebhookCreateResponseConflictsItem } from 'api';
import { Restricted } from 'containers/Restricted';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { Spinner } from '@atoms/Spinner/Spinner';

import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import { AddWebhookModal } from './AddWebhookModal';
import { BulkEditWebhooksModal } from './BulkEditWebhooksModal';
import type { ToggleMode } from './constants';
import {
  WEBHOOKS_ERROR_COPY,
  WEBHOOKS_ERROR_TEST_ID,
  WEBHOOKS_HEADER_CREATE_TEST_ID,
  WEBHOOKS_LOADING_TEST_ID,
  WEBHOOKS_PAGE_TEST_ID,
  WEBHOOKS_TITLE_TEST_ID
} from './constants';
import { ReactivateWebhookConflictModal } from './ReactivateWebhookConflictModal';
import { ToggleWebhookStatusModal } from './ToggleWebhookStatusModal';
import { WebhooksEmptyState } from './WebhooksEmptyState';
import { WebhooksTable } from './WebhooksTable';

import { EMPTY_STATE_MIN_HEIGHT } from '../constants';
import { DevelopersPageHeader } from '../DevelopersPageHeader';
import { useWebhooks } from '../hooks/useWebhooks';

export const Webhooks = () => {
  const { webhooks, isLoading, isError } = useWebhooks();
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<ToggleMode | null>(null);
  const [isBulkEditOpen, setBulkEditOpen] = useState(false);
  const [conflicts, setConflicts] = useState<
    WebhookCreateResponseConflictsItem[]
  >([]);
  const isEmpty = !isLoading && !isError && webhooks.length === 0;

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const editableWebhooks = useMemo(
    () => webhooks.filter((webhook) => webhook.editable),
    [webhooks]
  );

  const selectedWebhooks = useMemo(
    () => webhooks.filter((webhook) => selectedUuids.has(webhook.uuid)),
    [webhooks, selectedUuids]
  );

  const toggleRow = useCallback((uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedUuids((prev) =>
      prev.size === editableWebhooks.length
        ? new Set()
        : new Set(editableWebhooks.map((webhook) => webhook.uuid))
    );
  }, [editableWebhooks]);

  const clearSelection = useCallback(() => setSelectedUuids(new Set()), []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          data-testid={WEBHOOKS_LOADING_TEST_ID}
          className={`flex ${EMPTY_STATE_MIN_HEIGHT} items-center justify-center`}
        >
          <Spinner size="large" />
        </div>
      );
    }

    if (isError) {
      return (
        <div
          data-testid={WEBHOOKS_ERROR_TEST_ID}
          className="flex items-center justify-center py-12 text-content-secondary"
        >
          {WEBHOOKS_ERROR_COPY}
        </div>
      );
    }

    if (isEmpty) {
      return <WebhooksEmptyState onCreate={openModal} />;
    }

    return (
      <WebhooksTable
        selectedUuids={selectedUuids}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        onActivateAll={() => setBulkMode('activate')}
        onDeactivateAll={() => setBulkMode('deactivate')}
        onBulkEdit={() => setBulkEditOpen(true)}
      />
    );
  };

  return (
    <>
      <TableLayout
        containerTestId={WEBHOOKS_PAGE_TEST_ID}
        header={
          <DevelopersPageHeader
            title="Webhooks"
            titleTestId={WEBHOOKS_TITLE_TEST_ID}
            action={
              !isEmpty && (
                <Restricted requiredPermission="developers:manage">
                  <PrimaryButton
                    data-testid={WEBHOOKS_HEADER_CREATE_TEST_ID}
                    icon={<PlusIcon className="size-4" />}
                    onClick={openModal}
                  >
                    Add webhook
                  </PrimaryButton>
                </Restricted>
              )
            }
          />
        }
        content={renderContent()}
      />
      <AddWebhookModal
        open={isModalOpen}
        onClose={closeModal}
        onConflicts={setConflicts}
      />
      <ReactivateWebhookConflictModal
        conflicts={conflicts}
        open={conflicts.length > 0}
        onClose={() => setConflicts([])}
      />
      <ToggleWebhookStatusModal
        webhooks={selectedWebhooks}
        mode={bulkMode ?? 'deactivate'}
        open={bulkMode !== null}
        onCancel={() => setBulkMode(null)}
        onConfirmed={() => {
          setBulkMode(null);
          clearSelection();
        }}
      />
      <BulkEditWebhooksModal
        uuids={Array.from(selectedUuids)}
        open={isBulkEditOpen}
        onCancel={() => setBulkEditOpen(false)}
        onSaved={() => {
          setBulkEditOpen(false);
          clearSelection();
        }}
      />
    </>
  );
};
